import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { TaskDialog, Spinner } from '@repo/components';
import { Task } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyEvent, getCompanyEventTasks } from 'src/api/company';
import TasksTabById from 'src/parts/tabs/TasksTabById';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/events/$eventId/tasks/')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, eventId } = Route.useParams();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();
	const [selectedTask, setSelectedTask] = useState<Task | null>(null);

	const { data: event } = useQuery({
		queryKey: ['company-event', groupId, eventId],
		queryFn: () => getCompanyEvent(groupId, eventId),
		enabled: !userLoading,
	});

	const { data: tasks, isLoading: tasksLoading } = useQuery({
		queryKey: ['company-event-tasks', groupId, eventId],
		queryFn: () => getCompanyEventTasks(groupId, eventId),
		enabled: !userLoading,
	});

	useBreadcrumbLevel(3, 'Tasks');

	if (userLoading || tasksLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser) return null;

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['company-event', groupId, eventId] });
		queryClient.invalidateQueries({ queryKey: ['company-event-tasks', groupId, eventId] });
	};

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['company-event-tasks', groupId, eventId] });
	};

	const handleTaskDialogClose = () => {
		setSelectedTask(null);
		handleReload();
	};

	const handleTaskSelected = (id: string) => {
		const task = tasks?.data?.find((t) => t.id === id);
		if (!task) return;
		if (task.status === 'todo') {
			setSelectedTask(task);
		} else if (task.status === 'done') {
			navigate({
				to: '/company/$groupId/events/$eventId/tasks/$taskId',
				params: { groupId, eventId, taskId: id },
			});
		}
	};

	return (
		<>
			<TasksTabById
				tasks={tasks}
				authenticatedUser={authenticatedUser}
				onUpdate={handleUpdate}
				onReload={handleReload}
				onTaskSelected={handleTaskSelected}
			/>
			<TaskDialog
				task={selectedTask}
				onClose={handleTaskDialogClose}
				auth=""
				userId={authenticatedUser?.id || selectedTask?.userId}
				hideAppBar={false}
				showTitlePage={false}
				lng=""
				forCompanyPortal={true}
			/>
		</>
	);
}
